import { useContext, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useScrollToBottom } from '../../hooks'

import { ModalWrap } from '../modal/ModalWrap'
import { Content } from '../container/Content'
import { Button } from '../button'
import type { ButtonProps } from '../button/Button'
import { Footer } from '../footer'
import { Header } from '../header'
import { DetailContent } from '../pagingList'
import { ArtipioContents, ArtipioContentItem } from '../contents'

interface TermsDetailModalProps {
  onDismiss: (result: boolean) => void
  termsId?: number
  isScrollEvent?: boolean
  buttonProps?: ButtonProps & {
    footerContent?: string
  }
}

export const TermsDetailModal = observer(
  ({ termsId, onDismiss, isScrollEvent, buttonProps }: TermsDetailModalProps) => {
    const { commonStore } = useContext(StoresContext)
    const detailStore = commonStore.termsDetailStore

    const contentRef = useRef<HTMLDivElement>(null)

    const { isScrollBottom, setIsScrollBottom, checkDidMountScrollHeight, checkScrollToBottom } = useScrollToBottom()

    useEffect(() => {
      if (typeof termsId !== 'number') return
      loadData()
    }, [termsId])

    useEffect(() => {
      if (!detailStore.isLoaded) return

      getScrollInfo()
    }, [detailStore.isLoaded, detailStore.content])

    const loadData = async () => {
      try {
        await detailStore.load({ param: { id: termsId } }, true)
      } catch (err: unknown) {
        toast('페이지를 불러오는데 실패했습니다.', err)
      }
    }

    const getScrollInfo = async () => {
      if (!contentRef.current) return

      const { scrollHeight, clientHeight } = await contentRef.current
      if (!scrollHeight || !clientHeight) {
        setIsScrollBottom(true)
        return
      }

      checkDidMountScrollHeight(scrollHeight, clientHeight)
    }

    return (
      <ModalWrap>
        <Header type="MODAL" title="" onDismiss={() => onDismiss(false)} />
        <StyledContent ref={contentRef} onScroll={checkScrollToBottom}>
          <DetailContent detailStore={detailStore}>
            <ArtipioContents>
              {detailStore.content?.contents?.map((content, i) => {
                const isFirstImg = content.type === 'image' && i === 0
                const isLastImg = content.type === 'image' && i === Number(detailStore.content?.contents?.length) - 1
                return <ArtipioContentItem key={i} {...content} isFirstImg={isFirstImg} isLastImg={isLastImg} />
              })}
            </ArtipioContents>
          </DetailContent>
        </StyledContent>
        <Footer>
          <Button
            size="large"
            disabled={isScrollEvent && !isScrollBottom}
            block={true}
            onClick={() => onDismiss(true)}
            {...buttonProps}
          >
            {buttonProps?.footerContent || '확인'}
          </Button>
        </Footer>
      </ModalWrap>
    )
  },
)

const StyledContent = styled(Content)`
  padding-bottom: 71px;
`
